// GameScene: one room of a run. Assembles every per-room system into a
// GameContext, drives the update order, owns hitstop / slow motion / camera,
// and handles room → route map → next room transitions and run end.
// The scene restarts for every room; persistent state lives in RunState.

import Phaser from 'phaser';
import { audio } from '../audio';
import { MAX_DT, SCENES } from '../config/constants';
import type {
  GameContext,
  ResultsSceneData,
  RouteSceneData,
  SystemApi,
  TimeApi,
} from '../core/contracts';
import { bus, Subscriptions } from '../core/events';
import { clearRun, requireRun, type RunState } from '../core/run';
import { save } from '../core/save';
import { session } from '../core/session';
import { SECTORS } from '../data/sectors';
import { evaluateMilestones } from '../data/unlocks';
import { createAbilities } from '../systems/abilities';
import { createBosses } from '../systems/bosses';
import { createCombat } from '../systems/combat';
import { createEnemies } from '../systems/enemies';
import { createFx } from '../systems/fx';
import { createLoot } from '../systems/loot';
import { createPlayer } from '../systems/player';
import { createInput } from '../systems/player/input';
import { createWorld } from '../systems/world';
import { createInteract } from '../systems/world/interact';

/** Test hook: ?maxdt=0.25 lets slow headless renderers simulate at real speed (default MAX_DT). */
const STEP_CAP = (() => {
  const v = Number(new URLSearchParams(window.location.search).get('maxdt'));
  return Number.isFinite(v) && v > 0 ? Math.min(v, 0.5) : MAX_DT;
})();

export interface GameSceneData {
  nodeId?: string;
}

interface Clock {
  now: number;
  hitstopUntil: number;
  slowFactor: number;
  slowUntil: number;
}

/** Meta currency awarded at the end of a run. */
export function computeCores(run: RunState, victory: boolean): number {
  const s = run.stats;
  const base =
    10 +
    s.kills * 0.5 +
    s.eliteKills * 3 +
    s.bossKills * 30 +
    run.sectorIndex * 15 +
    s.challengesCompleted * 4 +
    (victory ? 60 : 0);
  // Each challenge option enabled in the hangar adds +20%.
  return Math.round(base * (1 + 0.2 * run.challenges.length));
}

export class GameScene extends Phaser.Scene {
  private ctx: GameContext | null = null;
  private systems: SystemApi[] = [];
  private subs = new Subscriptions();
  private clock: Clock = { now: 0, hitstopUntil: 0, slowFactor: 1, slowUntil: 0 };
  /** True while leaving the room (route map open, fading out, run ending). Blocks pause/map input. */
  private leaving = false;
  private lookX = 0;
  private lookY = 0;
  /** Smoothed combat intensity fed to the music engine (0 calm … 1 boss). */
  private intensity = 0;
  private intensitySent = -1;
  private intensityTimer = 0;

  constructor() {
    super(SCENES.GAME);
  }

  create(data: GameSceneData): void {
    const run = requireRun();
    run.enterNode(data?.nodeId ?? run.nodeId);
    const node = run.node;
    const sector = SECTORS[run.sectorIndex];

    this.leaving = false;
    this.lookX = 0;
    this.lookY = 0;
    this.intensityTimer = 0;
    this.clock = { now: 0, hitstopUntil: 0, slowFactor: 1, slowUntil: 0 };
    this.physics.world.resume();
    this.physics.world.timeScale = 1;
    this.tweens.timeScale = 1;
    this.time.timeScale = 1;

    const clock = this.clock;
    const time: TimeApi = {
      get now() {
        return clock.now;
      },
      hitstop(ms: number) {
        if (ms > 0) clock.hitstopUntil = Math.max(clock.hitstopUntil, performance.now() + ms);
      },
      get inHitstop() {
        return performance.now() < clock.hitstopUntil;
      },
      slowMo(factor: number, durationMs: number) {
        clock.slowFactor = Phaser.Math.Clamp(factor, 0.05, 1);
        clock.slowUntil = performance.now() + durationMs;
      },
      get scale() {
        return performance.now() < clock.slowUntil ? clock.slowFactor : 1;
      },
    };

    const input = createInput(this);
    const ctx = {
      scene: this,
      bus,
      run,
      rng: run.rng.combat,
      get settings() {
        return save.settings;
      },
      audio,
      time,
      input,
    } as unknown as GameContext;
    this.ctx = ctx;

    // Construction order: nothing may touch another system before start().
    ctx.fx = createFx(ctx);
    ctx.world = createWorld(ctx, node, sector);
    ctx.interact = createInteract(ctx);
    const combat = createCombat(ctx);
    ctx.projectiles = combat.projectiles;
    ctx.damage = combat.damage;
    ctx.status = combat.status;
    ctx.zones = combat.zones;
    ctx.effects = combat.effects;
    ctx.enemies = createEnemies(ctx);
    ctx.bosses = createBosses(ctx);
    ctx.loot = createLoot(ctx);
    ctx.player = createPlayer(ctx, ctx.world.playerSpawn);
    ctx.abilities = createAbilities(ctx);

    // Update order: input → world director → player → abilities → enemies → bosses
    // → projectiles → zones → status → item effects → damage → loot → interact → fx.
    this.systems = [
      input,
      ctx.world,
      ctx.player,
      ctx.abilities,
      ctx.enemies,
      ctx.bosses,
      ctx.projectiles,
      ctx.zones,
      ctx.status,
      ctx.effects,
      ctx.damage,
      ctx.loot,
      ctx.interact,
      ctx.fx,
    ];
    session.ctx = ctx;
    for (const s of this.systems) s.start?.();

    const cam = this.cameras.main;
    cam.setBounds(0, 0, ctx.world.width, ctx.world.height);
    cam.setBackgroundColor(sector.palette.skyBottom);
    cam.startFollow(ctx.player.bodyObject, false, 0.14, 0.14);
    cam.centerOn(ctx.player.x, ctx.player.y);
    cam.fadeIn(320, 5, 7, 11);

    if (!this.scene.isActive(SCENES.UI)) this.scene.launch(SCENES.UI);
    this.scene.bringToTop(SCENES.UI);

    this.subs.on('room:exitRequested', () => this.leaveRoom());
    this.subs.on('player:died', () => {
      this.leaving = true;
      this.time.delayedCall(1500, () => this.finishRun(false));
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());

    audio.music(node.type === 'boss' ? 'boss' : sector.music, { fadeMs: 900 });
    bus.emit('room:entered', { node, roomId: ctx.world.roomId });
  }

  update(_time: number, deltaMs: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const realDt = Math.min(deltaMs / 1000, STEP_CAP);
    ctx.input.update(realDt);

    if (!this.leaving && ctx.player.alive) {
      if (ctx.input.pressed('pause')) {
        this.openPause();
        return;
      }
      if (ctx.input.pressed('map')) {
        this.openMap();
        return;
      }
    }

    if (ctx.time.inHitstop) {
      if (!this.physics.world.isPaused) this.physics.world.pause();
      return;
    }
    if (this.physics.world.isPaused) this.physics.world.resume();

    const scale = ctx.time.scale;
    this.physics.world.timeScale = 1 / scale;
    this.tweens.timeScale = scale;
    const dt = realDt * scale;
    this.clock.now += dt;
    ctx.run.stats.timeSec += realDt;

    // systems[0] is input (already updated with real dt).
    for (let i = 1; i < this.systems.length; i++) this.systems[i].update(dt);
    this.updateCamera(realDt);
    this.updateMusicIntensity(realDt);
  }

  /** Music layers follow the fight: more enemies / a boss → more drums and lead. Throttled to 4 Hz. */
  private updateMusicIntensity(dt: number): void {
    const ctx = this.ctx!;
    const target = ctx.bosses.active ? 1 : Math.min(1, ctx.enemies.aliveCount() / 6);
    this.intensity += (target - this.intensity) * (1 - Math.exp(-dt * 1.2));
    this.intensityTimer -= dt;
    if (this.intensityTimer <= 0 && Math.abs(this.intensity - this.intensitySent) > 0.02) {
      this.intensityTimer = 0.25;
      this.intensitySent = this.intensity;
      audio.setIntensity(this.intensity);
    }
  }

  /** Aim/velocity lookahead so the player sees where they are shooting. */
  private updateCamera(dt: number): void {
    const ctx = this.ctx!;
    const p = ctx.player;
    const aim = ctx.input.pointerWorld;
    const tx = Phaser.Math.Clamp((aim.x - p.x) * 0.16 + p.vx * 0.06, -240, 240);
    const ty = Phaser.Math.Clamp((aim.y - p.y) * 0.12, -150, 150);
    const k = 1 - Math.exp(-dt * 5);
    this.lookX += (tx - this.lookX) * k;
    this.lookY += (ty - this.lookY) * k;
    this.cameras.main.setFollowOffset(-this.lookX, -this.lookY);
  }

  private storePlayerState(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.run.hp = Math.max(1, Math.round(ctx.player.hp));
    ctx.run.shield = ctx.player.shield;
    ctx.run.ultCharge = ctx.player.ultCharge;
    ctx.run.clampUltimate();
  }

  private leaveRoom(): void {
    const ctx = this.ctx;
    if (!ctx || this.leaving || !ctx.player.alive) return;
    const run = ctx.run;
    this.storePlayerState();

    if (run.node.type === 'boss') {
      if (run.isFinalSector) {
        this.finishRun(true);
        return;
      }
      bus.emit('run:sectorComplete', { sector: run.sectorIndex });
      run.advanceSector();
      this.transitionTo(run.route.startId);
      return;
    }

    this.leaving = true;
    ctx.player.setControlEnabled(false);
    this.scene.pause();
    const data: RouteSceneData = {
      graph: run.route,
      currentId: run.nodeId,
      sectorName: SECTORS[run.sectorIndex].name,
      sectorIndex: run.sectorIndex,
      onPick: (nodeId: string) => {
        this.scene.stop(SCENES.ROUTE);
        this.scene.resume();
        this.transitionTo(nodeId);
      },
      onClose: () => {
        this.scene.stop(SCENES.ROUTE);
        this.scene.resume();
        this.leaving = false;
        ctx.player.setControlEnabled(true);
        bus.emit('room:exitCancelled', {});
      },
    };
    this.scene.launch(SCENES.ROUTE, data);
    this.scene.bringToTop(SCENES.ROUTE);
  }

  private transitionTo(nodeId: string): void {
    this.leaving = true;
    const cam = this.cameras.main;
    cam.fadeOut(280, 5, 7, 11);
    cam.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.scene.restart({ nodeId } satisfies GameSceneData));
  }

  private openPause(): void {
    this.scene.pause();
    this.scene.launch(SCENES.PAUSE);
    this.scene.bringToTop(SCENES.PAUSE);
  }

  private openMap(): void {
    const run = this.ctx!.run;
    this.scene.pause();
    const data: RouteSceneData = {
      graph: run.route,
      currentId: run.nodeId,
      sectorName: SECTORS[run.sectorIndex].name,
      sectorIndex: run.sectorIndex,
      viewOnly: true,
      onPick: () => undefined,
      onClose: () => {
        this.scene.stop(SCENES.ROUTE);
        this.scene.resume();
      },
    };
    this.scene.launch(SCENES.ROUTE, data);
    this.scene.bringToTop(SCENES.ROUTE);
  }

  private finishRun(victory: boolean): void {
    const ctx = this.ctx;
    if (!ctx || ctx.run.ended) return;
    const run = ctx.run;
    run.ended = true;
    run.victory = victory;
    this.leaving = true;
    this.storePlayerState();
    bus.emit('run:ended', { victory });

    const cores = computeCores(run, victory);
    save.data.runs += 1;
    if (victory) save.data.wins += 1;
    save.data.bestSector = Math.max(save.data.bestSector, run.sectorIndex);
    save.data.totalKills += run.stats.kills;
    save.data.lastMech = run.mechId;
    save.addCores(cores); // persists
    const newUnlocks = evaluateMilestones(run, victory);

    const inv = run.inventory;
    const data: ResultsSceneData = {
      victory,
      stats: { ...run.stats },
      mechId: run.mechId,
      sector: run.sectorIndex,
      coresEarned: cores,
      build: {
        weapons: inv.weapons.map((w) => w?.defId ?? null),
        equipment: inv.visualIds(),
        mutations: inv.mutations.map((m) => m.defId),
        synergies: inv.activeSynergies(run.mech.kitTags),
      },
      newUnlocks,
    };

    const cam = this.cameras.main;
    cam.fadeOut(victory ? 900 : 600, 0, 0, 0);
    cam.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      this.scene.stop(SCENES.UI);
      clearRun();
      this.scene.start(SCENES.RESULTS, data);
    });
  }

  private teardown(): void {
    this.subs.dispose();
    // Phaser has already destroyed this scene's display list by now; destroy() must tolerate that.
    for (const s of [...this.systems].reverse()) {
      try {
        s.destroy();
      } catch (err) {
        console.error('[game] system destroy failed', err);
      }
    }
    this.systems = [];
    if (session.ctx === this.ctx) session.ctx = null;
    this.ctx = null;
    // On stop (not restart) Phaser may already have disposed the physics world.
    if (this.physics?.world) this.physics.world.timeScale = 1;
    if (this.tweens) this.tweens.timeScale = 1;
  }
}
